import { darken, fade, makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: theme.spacing(1, 0, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    minWidth: 0,
    overflow: 'hidden',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2)
  },
  titleGroup: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    minWidth: 0
  },
  headerIcon: {
    fontSize: 20,
    marginTop: 2,
    color: theme.palette.text.secondary
  },
  title: { fontSize: 16, fontWeight: 600, color: theme.palette.text.primary },
  subtitle: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  body: { padding: theme.spacing(2), minWidth: 0 },
  composer: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default
    },
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1.5) }
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(1.5)
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  historyCount: { color: theme.palette.text.secondary, fontSize: 12 },
  historyList: { display: 'grid', gap: theme.spacing(1.5) },
  note: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    minWidth: 0,
    overflow: 'hidden'
  },
  noteHeader: {
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  noteKind: { fontSize: 13, fontWeight: 600 },
  noteDate: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 400
  },
  noteBody: { padding: theme.spacing(2), minWidth: 0 },
  text: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    lineHeight: 1.9,
    fontSize: 13
  },
  targets: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
    alignItems: 'center',
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  targetChip: {
    height: 24,
    fontSize: 11,
    maxWidth: '100%',
    '& .MuiChip-label': { overflowWrap: 'anywhere', overflow: 'visible' }
  },
  stateChip: {
    height: 'auto',
    minHeight: 26,
    maxWidth: '100%',
    fontSize: 11,
    fontWeight: 500,
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      lineHeight: 1.6,
      padding: theme.spacing(0.25, 1)
    }
  },
  delivered: {
    backgroundColor: fade(theme.palette.success.main, 0.1),
    color:
      theme.palette.type === 'dark'
        ? theme.palette.success.light
        : theme.palette.success.dark
  },
  attention: {
    backgroundColor: fade(theme.palette.warning.main, 0.1),
    color:
      theme.palette.type === 'dark'
        ? theme.palette.warning.light
        : darken(theme.palette.warning.dark, 0.2)
  },
  failed: {
    backgroundColor: fade(theme.palette.error.main, 0.1),
    color:
      theme.palette.type === 'dark'
        ? theme.palette.error.light
        : theme.palette.error.dark
  },
  deliveryToggle: {
    '&.MuiButton-textPrimary': {
      color:
        theme.palette.type === 'dark'
          ? theme.palette.primary.light
          : theme.palette.primary.main
    },
    color:
      theme.palette.type === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.main,
    '&:focus-visible, &.Mui-focusVisible': {
      outline: `2px solid ${
        theme.palette.type === 'dark'
          ? theme.palette.primary.light
          : theme.palette.primary.main
      }`,
      outlineOffset: 2,
      borderRadius: 4
    },
    marginTop: theme.spacing(1),
    textTransform: 'none',
    fontSize: 12,
    padding: theme.spacing(0.5, 0),
    '& svg': { transition: theme.transitions.create('transform') }
  },
  expandedIcon: { transform: 'rotate(180deg)' },
  deliveryList: {
    margin: theme.spacing(1, 0, 0),
    padding: 0,
    listStyle: 'none',
    borderTop: `1px solid ${theme.palette.divider}`
  },
  deliveryRow: {
    padding: theme.spacing(1.5, 0),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    '& + &': { borderTop: `1px solid ${theme.palette.divider}` }
  },
  deliveryMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.text.secondary,
    minWidth: 0,
    '& bdi': { overflowWrap: 'anywhere' }
  },
  deliveryRoom: { color: theme.palette.text.primary, fontWeight: 500 },
  feedback: {
    '&:not(:empty)': {
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: 6,
      backgroundColor: fade(theme.palette.info.main, 0.06)
    },
    '& p': { fontSize: 12 }
  },
  error: {
    color: theme.palette.error.main,
    backgroundColor: fade(theme.palette.error.main, 0.06),
    padding: theme.spacing(1.5),
    borderRadius: 6,
    marginBottom: theme.spacing(1.5),
    fontSize: 12
  },
  empty: {
    padding: theme.spacing(3, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    fontSize: 13
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  primaryAction: {
    minWidth: 120,
    order: 2,
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: { width: '100%' }
  },
  refresh: { textTransform: 'none', flexShrink: 0 },
  retryHint: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: 12
  }
}))

export default useStyles
